import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ANY_CONTEXT, GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { MicrosoftApiErrorLog } from '../entities/MicrosoftApiErrorLog.js';
import { MicrosoftGroupState } from '../entities/MicrosoftGroupState.js';
import { MicrosoftUserState } from '../entities/MicrosoftUserState.js';
import { MicrosoftRetryableError } from '../server/microsoft-graph-client.js';
import { refreshMicrosoftGroupState, refreshMicrosoftUserState } from '../server/microsoft-state-refresh.js';
import { enqueueMicrosoftGroupSync } from '../server/microsoft-sync-queue.js';
import { resolveGetDesiredEntraGroupsForPerson } from '../server/resolvers/resolveGetDesiredEntraGroupsForPerson.js';
import { resolveGetMicrosoftStateForGroup } from '../server/resolvers/resolveGetMicrosoftStateForGroup.js';
import { resolveGetMicrosoftStateForPerson } from '../server/resolvers/resolveGetMicrosoftStateForPerson.js';
import { resolveGetRecentMicrosoftErrors } from '../server/resolvers/resolveGetRecentMicrosoftErrors.js';
import { repairGroupInEntra } from '../server/self-repair/repairGroupInEntra.js';
import { repairUserInEntra } from '../server/self-repair/repairUserInEntra.js';
import { resetMicrosoftUserPassword } from '../server/users/microsoft-reset-password.js';

const subscribeMicrosoftStateForPerson = registerSubscription({
  operationName: 'subscribeMicrosoftStateForPerson',
  type: () => MicrosoftUserState,
  params: [{ type: () => ID, name: 'userID' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveGetMicrosoftStateForPerson,
});

/**
 * The groups a person belongs to according to us, as Entra is supposed to name them.
 *
 * ! The tab must not work this out itself. !
 *
 * It used to, from the person's bookings, and those are filtered by what the caller may see - so a
 * group in a school they may not read was missing from our side of the comparison and the person looked
 * out of sync to them while being perfectly in sync. See `getDesiredEntraGroupsForPerson` for what that
 * cost on the repair side.
 */
const getDesiredEntraGroupsForPersonQuery = registerQuery({
  operationName: 'getDesiredEntraGroupsForPerson',
  type: () => [String],
  params: [{ type: () => ID, name: 'userID' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveGetDesiredEntraGroupsForPerson,
});

const subscribeMicrosoftStateForGroup = registerSubscription({
  operationName: 'subscribeMicrosoftStateForGroup',
  type: () => MicrosoftGroupState,
  params: [{ type: () => ID, name: 'groupID' }],
  middlewares: [authenticatedMiddleware],
  resolve: resolveGetMicrosoftStateForGroup,
});

/**
 * Reads the current state from Entra and stores it, on demand.
 *
 * ! This is what the refresh button calls. It must never delete the snapshot instead. !
 *
 * Deleting it used to be the refresh: the old resolver re-read Entra whenever it noticed the snapshot
 * was missing, so removing the document made it rebuild. That re-read happened *inside* the watch on
 * the collection it wrote to, which is the feedback loop that took the server down - so it is gone, and
 * with it the only thing that would have brought the deleted snapshot back before the tab is reopened.
 * Refreshing is an explicit call now, and this is it.
 *
 * Only authentication is checked: this reads from Entra and writes nothing back to it, so anyone who
 * may look at the tab may re-read it.
 */
const refreshMicrosoftStateForPersonMutation = registerMutation({
  operationName: 'refreshMicrosoftStateForPerson',
  type: () => Boolean,
  params: [{ type: () => ID, name: 'userID' }],
  middlewares: [authenticatedMiddleware],
  resolve: (userID: string) => async () => {
    await refreshMicrosoftUserState(userID);
    return true;
  },
});

/** @see refreshMicrosoftStateForPersonMutation */
const refreshMicrosoftStateForGroupMutation = registerMutation({
  operationName: 'refreshMicrosoftStateForGroup',
  type: () => Boolean,
  params: [{ type: () => ID, name: 'groupID' }],
  middlewares: [authenticatedMiddleware],
  resolve: (groupID: string) => async () => {
    await refreshMicrosoftGroupState(groupID);
    return true;
  },
});

/**
 * Repairs run straight away instead of going through the sync queue: somebody is looking at the tab and
 * wants to see whether it worked. Everything that happens *without* a person watching is queued
 * instead - see `microsoft-sync-queue.ts`.
 *
 * The permission is checked here and not only in the client. The button was already hidden without it,
 * but hiding a button is not a permission check - the mutation used to accept any logged in account.
 *
 * ! A repair reports that it ran, not what it produced. !
 *
 * The refreshed snapshot reaches the tab through the state subscription above, so returning it here as
 * well would send the same document twice. It also cannot be returned safely: the callers invoke the
 * mutation without a selection set, and an entity that comes back from the network with no fields
 * requested throws as soon as anything touches one - which is what "Trying to access property _id on
 * MicrosoftUserState, but it was not requested from remote" meant.
 */
const repairUserInEntraMutation = registerMutation({
  operationName: 'repairUserInEntra',
  type: () => Boolean,
  params: [{ type: () => ID, name: 'userID' }],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_PERSON_MICROSOFT_ACCOUNT, ANY_CONTEXT)],
  resolve: (userID: string) => async () => {
    // a person clicked repair, so this may move an existing mailbox to the address the portal says it
    // should have whatever it held before. The scheduled sync only moves it along with the person's
    // schools and reports any other difference - see `mayWritePrimaryMail`.
    try {
      await repairUserInEntra(userID, { mayMovePrimaryMail: true });
    } catch (error) {
      // Microsoft asked us to wait (throttling, a short outage). That is a message for the person who
      // clicked, not a defect - the queue handles the same answer with a delayed retry.
      if (!(error instanceof MicrosoftRetryableError)) throw error;
      throw new ExpectedError(`${error.message} Bitte in ein paar Minuten erneut reparieren.`);
    }
    return true;
  },
});

/** @see repairUserInEntraMutation */
const repairGroupInEntraMutation = registerMutation({
  operationName: 'repairGroupInEntra',
  type: () => Boolean,
  params: [{ type: () => ID, name: 'groupID' }],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_PERSON_MICROSOFT_ACCOUNT, ANY_CONTEXT)],
  resolve: (groupID: string) => async () => {
    try {
      await repairGroupInEntra(groupID);
    } catch (error) {
      if (!(error instanceof MicrosoftRetryableError)) throw error;

      // throttling, or a group that was just created and is not readable yet: the queue finishes the
      // repair with its delayed retry, so the person who clicked gets a message instead of a failure.
      // ponytail: JetStream drops this push if the same group was pushed less than 2 minutes ago - the
      // message still asks for another click, which covers it.
      await enqueueMicrosoftGroupSync(groupID);
      throw new ExpectedError(`${error.message} Bitte in ein paar Minuten prüfen und ggf. erneut reparieren.`);
    }
    return true;
  },
});

/**
 * Sets a one-time password on the person's Entra account and mails it to them. Same gate as the
 * repair: whoever may repair the account may reset its password. Returns only that it ran, for the
 * same selection-set reason as {@link repairUserInEntraMutation}.
 */
const resetMicrosoftUserPasswordMutation = registerMutation({
  operationName: 'resetMicrosoftUserPassword',
  type: () => Boolean,
  params: [{ type: () => ID, name: 'userID' }],
  middlewares: [checkPermissionMiddleware(Permission.EDIT_PERSON_MICROSOFT_ACCOUNT, ANY_CONTEXT)],
  resolve: (userID: string) => async () => {
    await resetMicrosoftUserPassword(userID);
    return true;
  },
});

const getRecentMicrosoftErrorsQuery = registerQuery({
  operationName: 'getRecentMicrosoftErrors',
  type: () => [MicrosoftApiErrorLog],
  params: [{ type: () => Int, name: 'limit' }],
  middlewares: [checkPermissionMiddleware(Permission.VIEW_ADMIN_DASHBOARD, GLOBAL_CONTEXT)],
  resolve: resolveGetRecentMicrosoftErrors,
});

export const MicrosoftAPI = {
  subscribeStateForUser: subscribeMicrosoftStateForPerson,
  subscribeStateForGroup: subscribeMicrosoftStateForGroup,
  getDesiredGroupsForUser: getDesiredEntraGroupsForPersonQuery,
  refreshStateForUser: refreshMicrosoftStateForPersonMutation,
  refreshStateForGroup: refreshMicrosoftStateForGroupMutation,
  repairUser: repairUserInEntraMutation,
  repairGroup: repairGroupInEntraMutation,
  resetUserPassword: resetMicrosoftUserPasswordMutation,
  getRecentErrors: getRecentMicrosoftErrorsQuery,
};
